import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import api, { fmtDate, formatApiError } from "../lib/api";
import { Trash2, RotateCcw, AlertTriangle, Loader2, Recycle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/rbac";

const TYPE_LABEL = {
    supplier: "Tedarikçi",
    product: "Ürün",
    project: "Proje",
    pr: "Satın Alma Talebi",
    rfq: "RFQ",
    po: "Sipariş",
    gr: "Mal Kabul",
    invoice: "Fatura",
};

export default function RecycleBin() {
    const { user } = useAuth();
    const canRestore = can(user, "recycle.restore");
    const canPermanent = can(user, "recycle.permanent");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState("");
    const [query, setQuery] = useState("");
    const [confirmPermanent, setConfirmPermanent] = useState(null); // {entity_type, id, display}
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/recycle-bin");
            setItems(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
            setItems([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const restore = async (row) => {
        setBusy(true);
        try {
            await api.post(`/recycle-bin/${row.entity_type}/${row.id}/restore`);
            toast.success("Kayıt geri yüklendi");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    const doPermanent = async () => {
        if (!confirmPermanent) return;
        setBusy(true);
        try {
            await api.delete(`/recycle-bin/${confirmPermanent.entity_type}/${confirmPermanent.id}`);
            toast.success("Kayıt kalıcı olarak silindi");
            setConfirmPermanent(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    const filtered = items.filter(i => {
        if (typeFilter && i.entity_type !== typeFilter) return false;
        if (!query) return true;
        const k = query.toLowerCase();
        return (i.display || "").toLowerCase().includes(k)
            || (i.number || "").toLowerCase().includes(k)
            || (i.deleted_by_name || "").toLowerCase().includes(k);
    });

    return (
        <AppShell title="Geri Dönüşüm Kutusu" subtitle="Sistem">
            <div className="bg-card border border-border rounded-md">
                <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
                    <div className="w-72">
                        <Input
                            data-testid="recycle-search"
                            placeholder="Ara: kayıt adı, numara, kullanıcı..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                    <select
                        data-testid="recycle-type-filter"
                        className="h-10 border border-input rounded-md px-3 bg-background text-sm"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                    >
                        <option value="">Tüm kayıt türleri</option>
                        {Object.entries(TYPE_LABEL).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                    <div className="text-xs text-muted-foreground ml-auto flex items-center gap-4">
                        <span>{filtered.length} silinmiş kayıt</span>
                        <button onClick={load} className="hover:text-foreground" title="Yenile">
                            <RotateCcw size={14}/>
                        </button>
                    </div>
                </div>

                {items.length === 0 && !loading ? (
                    <div className="py-16 text-center text-muted-foreground">
                        <Recycle size={40} className="mx-auto mb-3 opacity-30"/>
                        <div className="font-medium text-foreground text-sm">Geri dönüşüm kutusu boş</div>
                        <div className="text-xs mt-1">Silinen kayıtlar burada görüntülenir ve geri yüklenebilir</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Tür</th>
                                    <th>Kayıt</th>
                                    <th>Silen</th>
                                    <th>Silinme Tarihi</th>
                                    <th className="text-right">İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={5} className="text-center py-12 text-muted-foreground">
                                        <Loader2 size={22} className="mx-auto mb-2 animate-spin opacity-70"/>
                                        Yükleniyor...
                                    </td></tr>
                                ) : filtered.map(row => (
                                    <tr key={`${row.entity_type}-${row.id}`} data-testid={`recycle-row-${row.entity_type}-${row.number || row.id}`}>
                                        <td>
                                            <span className="text-xs uppercase tracking-wider px-2 py-0.5 bg-secondary rounded font-medium">
                                                {TYPE_LABEL[row.entity_type] || row.entity_type}
                                            </span>
                                        </td>
                                        <td className="font-medium">{row.display}</td>
                                        <td className="text-sm">{row.deleted_by_name || "—"}</td>
                                        <td className="text-sm text-muted-foreground">{fmtDate(row.deleted_at)}</td>
                                        <td className="text-right">
                                            <div className="inline-flex gap-1.5">
                                                {canRestore && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        data-testid={`restore-${row.entity_type}-${row.number || row.id}`}
                                                        onClick={() => restore(row)}
                                                        disabled={busy}
                                                        className="h-8 gap-1.5"
                                                    >
                                                        <RotateCcw size={13}/> Geri Yükle
                                                    </Button>
                                                )}
                                                {canPermanent && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        data-testid={`permanent-${row.entity_type}-${row.number || row.id}`}
                                                        onClick={() => setConfirmPermanent(row)}
                                                        disabled={busy}
                                                        className="h-8 gap-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/20"
                                                    >
                                                        <Trash2 size={13}/> Kalıcı Sil
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && filtered.length === 0 && items.length > 0 && (
                                    <tr><td colSpan={5} className="text-center py-12 text-muted-foreground text-sm">
                                        Filtreye uyan kayıt yok
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Dialog open={!!confirmPermanent} onOpenChange={() => setConfirmPermanent(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-rose-600">
                            <AlertTriangle size={20}/> Kalıcı Silme Onayı
                        </DialogTitle>
                        <DialogDescription className="pt-3 space-y-2">
                            <div>
                                <b className="text-foreground">Bu işlem geri alınamaz.</b> Kayıt veritabanından kalıcı olarak silinecektir.
                            </div>
                            {confirmPermanent && (
                                <div className="bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900 rounded p-3 mt-3">
                                    <div className="text-xs uppercase tracking-widest text-rose-700 dark:text-rose-400 mb-1">Silinecek Kayıt</div>
                                    <div className="text-sm font-medium text-foreground">
                                        {TYPE_LABEL[confirmPermanent.entity_type]} — {confirmPermanent.display}
                                    </div>
                                </div>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmPermanent(null)}>Vazgeç</Button>
                        <Button
                            variant="destructive"
                            data-testid="confirm-permanent-delete"
                            onClick={doPermanent}
                            disabled={busy}
                        >
                            {busy && <Loader2 size={14} className="mr-2 animate-spin"/>}
                            Kalıcı Olarak Sil
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
